/**
 * Observed reachability — the truth layer over `navigator.onLine`.
 *
 * `navigator.onLine` is unreliable in both directions, but the dangerous one is
 * stale-TRUE: machines with virtual network adapters (Docker/WSL/Hyper-V/VPN)
 * report online with the wifi off, so the `offline` event never fires, the
 * provider believes it's online, and an offline vet sees no offline banner
 * while every network call fails. The engine already CLASSIFIES real
 * connectivity failures on its network paths (executeWrite fallthrough,
 * withOfflineFallback fallback, the provider's sync pull) — this module records
 * those observations so the app can derive EFFECTIVE onlineness:
 * `navigator.onLine && no observed-down condition`.
 *
 * Self-correcting in both directions:
 *  - any reachable response — success OR a real HTTP error (the server
 *    answered) — clears the observed-down state immediately;
 *  - the provider probes (delta pull) every 30s while observed-down with the
 *    adapter claiming online, so recovery doesn't wait for user action;
 *  - the browser 'online' event optimistically clears it (and the sync it
 *    triggers re-reports failure if the event lied).
 */

/**
 * A CONNECTIVITY failure (the request never got an HTTP response: dropped
 * network, dead/unreachable API, captive portal, request blocked) vs a real
 * server reject (4xx/5xx — it responded, just with an error). This is the
 * PER-CALL fallthrough rule (queue the write / serve the replica) — broad on
 * purpose, the pre-existing contract.
 */
export function isConnectivityError(e: any): boolean {
  return !e?.response;
}

/**
 * Does this axios error carry EVIDENCE of a real timeout, as opposed to an
 * abort? axios collapses both onto `ECONNABORTED`, so the code alone cannot
 * separate them — the MESSAGE can: axios builds `timeout of {ms}ms exceeded`
 * for a timeout and `Request aborted` for an abort.
 *
 * `config.timeout` is NOT a discriminator here: `utils/api.ts` creates the app's
 * axios instance with `timeout: 30000`, so an aborted request carries the same
 * non-zero timeout a genuine timeout does. The message is the only evidence.
 */
function isAxiosTimeout(e: any): boolean {
  return /timeout/i.test(String(e?.message ?? ''));
}

/**
 * Strict TRANSPORT-level classifier for GLOBAL state reporting: an axios
 * request that went out and never got a response (wifi off, dead API,
 * timeout). LOCAL errors — Dexie failures, code bugs (TypeError), axios
 * cancellations — must NEVER flip app-wide connectivity, or a deterministic
 * replica error would fake-offline a perfectly connected device forever.
 *
 * WHY `ECONNABORTED` needs its own arm — do NOT "simplify" this back to a code
 * check: as of **axios 1.20** the XHR adapter rejects a request torn down by
 * PAGE NAVIGATION with `ECONNABORTED` (earlier versions resolved it with status
 * 0). Programmatic aborts still carry `ERR_CANCELED`, so that code alone no
 * longer covers cancellation. Every deliberate away-navigation with a request in
 * flight — Stripe Checkout / billing portal (`pages/Paywall.tsx`), Stripe
 * Connect + OAuth (`pages/Settings/IntegrationsSettings.tsx`), QuickBooks OAuth
 * (`pages/Settings/QuickbooksConnectCard.tsx`) — would otherwise declare the
 * whole app offline on its way out, which is exactly what the contract above
 * forbids. So `ECONNABORTED` counts only when it looks like a real timeout.
 * (`ETIMEDOUT` — axios's `transitional.clarifyTimeoutError` form — is
 * unambiguous and falls through to the general case.)
 */
export function isTransportError(e: any): boolean {
  if (!e?.isAxiosError || e?.response) return false;
  if (e?.code === 'ERR_CANCELED') return false; // programmatic cancellation — neutral
  if (e?.code === 'ECONNABORTED') return isAxiosTimeout(e); // timeout yes, abort no
  return true;
}

let observedDown = false;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* a listener error must not break connectivity reporting */
    }
  });
}

export function reportConnectivityFailure(): void {
  if (observedDown) return;
  observedDown = true;
  // Flip log: a machine that flaps here every ~30s is diagnosable from console.
  // eslint-disable-next-line no-console
  console.warn(`[offline] connectivity observed DOWN (navigator.onLine=${navigator.onLine})`);
  emit();
}

export function reportConnectivitySuccess(): void {
  if (!observedDown) return;
  observedDown = false;
  // eslint-disable-next-line no-console
  console.warn('[offline] connectivity observed UP');
  emit();
}

/** Classify-and-report in one step for catch blocks on network paths: a
 *  TRANSPORT failure marks us down; an HTTP error is PROOF of reachability;
 *  anything else (a local/code error) is NEUTRAL — not a connectivity
 *  observation in either direction. */
export function reportFromError(e: unknown): void {
  if (isTransportError(e)) reportConnectivityFailure();
  else if ((e as any)?.response) reportConnectivitySuccess();
}

export function isEffectivelyOnline(): boolean {
  return navigator.onLine && !observedDown;
}

/** Subscribe to observed-connectivity flips. Returns the unsubscribe fn. */
export function subscribeConnectivity(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
